import { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  Drawer,
  SidebarGroup,
  SidebarItem,
  SidebarTopLevel,
  SidebarAction,
  type SidebarActionItem,
  ScopeBarExpanded,
  StageChip,
} from '@aigovops/design-system';
import { useAuth } from '../../lib/auth';
import { useOptionalWorkspace, type ScopeBarPayload } from '../../lib/workspace';
import { useAlertsCount } from '../../hooks/useAlertsCount';
import { useQuery } from '@tanstack/react-query';
import { monitoringKeys } from '../../hooks/useMonitoring';
import { getMonitoringBadge } from '../../lib/monitoring-summary';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { SectionNav } from './SectionNav';
import { navIcon } from './nav-icons';
import {
  STAFF_NAV_GROUP,
  NEW_ACTIONS,
  findActiveItem,
  isProgramKey,
  type NavGroup,
  type NavTopLevel,
  type NavItem,
  type ProgramNavItem,
} from '../../lib/nav-config';
import { useSidebarState } from '../../lib/sidebar-state';
import { useWorkspaceNavigation, type ShellKind } from '../../lib/workspace-navigation';
import { useWorkspaceEvents } from '../../hooks/useWorkspaceEvents';

/**
 * AppShell — sidebar layout (Phase 2) + mobile drawer/header/tabbar (Phase 3).
 *
 * V98-3-1: viewer-scoped shell (see lib/workspace-navigation.tsx) — the
 * program spine for leads / members, a minimal My items shell for
 * owner-only viewers, the assessor nav for assessor sessions. No role
 * switcher, no layout preference.
 *
 * Desktop (>=860px): 240px fixed sidebar + main content.
 * Mobile (<=859px): slim header + full-height drawer + bottom tab bar
 * (Today · Programs · Menu; owner-only: Today · More). Tabs carry no badges.
 *
 * All colors route through `var(--*)` tokens. No hardcoded hex.
 */

// -----------------------------------------------------------------------
// Mobile breakpoint
// -----------------------------------------------------------------------

/** V98-3-1 [R3]: the tab bar shows at <= 859px. */
export const MOBILE_BREAKPOINT_PX = 860;

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const q = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const update = () => setIsMobile(q.matches);
    update();
    q.addEventListener('change', update);
    return () => q.removeEventListener('change', update);
  }, []);
  return isMobile;
}

// -----------------------------------------------------------------------
// Helpers preserved from v1
// -----------------------------------------------------------------------

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function shortDisplayName(user: { email?: string | null; name?: string | null }): string {
  if (user.name) {
    const parts = user.name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  }
  const email = user.email ?? 'user';
  return email.split('@')[0];
}

/**
 * Format a scope-bar payload into <ScopeBar> props. Pure function
 * exported for use elsewhere.
 */
export function formatScopeBar(
  workspaceName: string,
  p: ScopeBarPayload,
  nowMs: number
): {
  modeLabel: string;
  workspaceFact: string;
  engagementWindow: string;
  progressTicker: string;
  right: string;
  mode: 'read-only' | 'live';
} {
  const modeLabel =
    p.phase === 'read_only' ? 'Read-only · archive window' : 'Read-only snapshot';
  const dateFmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  const pinDate = dateFmt(p.invited_at);
  const inviter =
    p.invited_by_name && p.invited_by_name.trim().length > 0
      ? shortDisplayName({ name: p.invited_by_name })
      : 'workspace admin';
  const workspaceFact = `${workspaceName} · pinned ${pinDate} by ${inviter}`;
  const engagementWindow = `Engagement ${dateFmt(p.engagement_start)} – ${dateFmt(
    p.engagement_end
  )}`;
  const startMs = Date.parse(p.engagement_start);
  const endMs = Date.parse(p.engagement_end);
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const totalWeeks = Math.max(1, Math.ceil((endMs - startMs) / weekMs));
  const rawWeek = Math.ceil((nowMs - startMs) / weekMs);
  const currentWeek = Math.min(Math.max(rawWeek, 1), totalWeeks);
  const progressTicker = `Week ${currentWeek} of ${totalWeeks}`;
  const right = `${p.firm_name} · 3PAO`;
  return {
    modeLabel,
    workspaceFact,
    engagementWindow,
    progressTicker,
    right,
    mode: 'read-only',
  };
}

/**
 * Format a scope-bar payload into <ScopeBarExpanded> props for the
 * assessor sidebar layout (NAV-5-2). Returns framework, period, status,
 * and optional package hash/verify link.
 */
export function formatScopeBarExpanded(
  p: ScopeBarPayload,
): {
  framework: string;
  periodStart: string;
  periodEnd: string;
  packageHash: string | undefined;
  verifyHref: string | undefined;
  status: 'active' | 'closed' | 'disputed';
} {
  const monthFmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
    });
  const framework = p.framework_name ?? 'Assessment';
  const periodStart = monthFmt(p.engagement_start);
  const periodEnd = monthFmt(p.engagement_end);
  const packageHash = p.package_hash ?? undefined;
  const verifyHref = p.package_id ? `/verify/${p.package_id}` : undefined;

  let status: 'active' | 'closed' | 'disputed';
  if (p.has_dispute) {
    status = 'disputed';
  } else if (p.phase === 'read_only') {
    status = 'closed';
  } else {
    status = 'active';
  }

  return { framework, periodStart, periodEnd, packageHash, verifyHref, status };
}

// -----------------------------------------------------------------------
// Badge resolution
// -----------------------------------------------------------------------

type BadgeSource = 'alerts' | 'my-work' | 'evidence-requests' | 'disputes' | 'questionnaires' | 'suppliers' | 'monitoring';

/**
 * Nav badges. 'my-work' is the viewer's owed count from the shared counter
 * (GET /me/counts) — the same number Today and the program pages show.
 * Other sources stay unbadged until wired (no unscoped numbers in chrome).
 */
function useBadgeCounts(slug: string | undefined, isAssessor: boolean, owed: number | null) {
  const alertsCriticalCount = useAlertsCount(isAssessor ? undefined : slug);
  // V113-7-2: open monitoring issues for the badge — ONE count query (GET /monitoring/badge), never the
  // per-system summary: the badge renders on every page, and the summary computes coverage per system.
  const monitoring = useQuery({
    queryKey: monitoringKeys.badge(slug ?? ''), queryFn: () => getMonitoringBadge(slug!),
    enabled: !!slug && !isAssessor, retry: false, staleTime: 60_000,
  });
  const openMonitoring = monitoring.data?.open_issues ?? 0;

  return useMemo(
    () => ({
      resolve(source: BadgeSource | undefined): number | undefined {
        if (source === 'my-work') return owed && owed > 0 ? owed : undefined;
        if (source === 'monitoring') return openMonitoring > 0 ? openMonitoring : undefined;
        return undefined;
      },
      resolveTitle(source: BadgeSource | undefined): string | undefined {
        if (source === 'my-work' && owed && owed > 0) return `${owed} owed by you`;
        if (source === 'monitoring' && openMonitoring > 0) return `${openMonitoring} open monitoring ${openMonitoring === 1 ? 'finding' : 'findings'}`;
        return undefined;
      },
      /** Direct access for the mobile header. */
      alertsCritical: alertsCriticalCount,
    }),
    [alertsCriticalCount, owed, openMonitoring]
  );
}

/** Per-period stage chips under a program row ("2026 · Audit", "2027 · ↻ Operate"). */
function ProgramMeta({ item }: { item: ProgramNavItem }) {
  if (item.absent) return <span>Not started · Add program</span>;
  return (
    <>
      {item.periods.map((p, i) => (
        <span key={`${p.label}:${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>{p.label}</span>
          {p.stages.map((st) => <StageChip key={st} stage={st} />)}
        </span>
      ))}
    </>
  );
}

function isProgramItem(item: NavItem): item is ProgramNavItem {
  return 'program' in item;
}

// -----------------------------------------------------------------------
// SidebarNav sub-component (desktop + drawer shared renderer)
// -----------------------------------------------------------------------

function SidebarNav({
  prefix,
  groups,
  kind,
  isStaff,
  isAssessor,
  badges,
  user,
  settingsActive,
  workspaceSlug,
  onNavigate,
}: {
  prefix: string;
  groups: (NavGroup | NavTopLevel)[];
  kind: ShellKind;
  isStaff: boolean;
  isAssessor: boolean;
  badges: ReturnType<typeof useBadgeCounts>;
  user: { email?: string | null; name?: string | null; role?: string } | null;
  settingsActive: boolean;
  workspaceSlug: string;
  /** Optional callback after navigation (mobile: closes drawer). */
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const loc = useLocation();

  // Compute relative path from workspace prefix for active-item detection
  const appPrefix = `${prefix}/`;
  const relativePath = loc.pathname.startsWith(appPrefix)
    ? loc.pathname.slice(appPrefix.length)
    : '';

  // Merge groups: main + staff editorial if applicable
  const allGroups = useMemo(() => {
    const result = [...groups];
    if (isStaff && !isAssessor) {
      result.push(STAFF_NAV_GROUP);
    }
    return result;
  }, [groups, isStaff, isAssessor]);

  const activeResult = findActiveItem(allGroups, relativePath);

  // NAV-2-5: controlled sidebar expand/collapse with localStorage persistence
  const sidebar = useSidebarState(workspaceSlug, allGroups, activeResult?.groupId ?? null);

  // "+ New" actions
  const actionItems: SidebarActionItem[] = useMemo(
    () =>
      NEW_ACTIONS.map((a) => ({
        label: a.label,
        icon: navIcon(a.icon),
        onClick: () => {
          navigate(`${prefix}/${a.href}`);
          onNavigate?.();
        },
      })),
    [navigate, prefix, onNavigate]
  );

  const displayName = user ? shortDisplayName(user) : 'User';
  const avatarInitials = user
    ? initialsOf(user.name || user.email || 'User')
    : 'U';
  const roleLabel = isAssessor ? 'Assessor' : (user?.role ?? '');
  // Settings is a nav item in the workspace shells; the assessor nav keeps it in the footer.
  const settingsInNav = allGroups.some((g) => g.id === 'settings');

  const handleNav = useCallback(
    (href: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      navigate(href);
      onNavigate?.();
    },
    [navigate, onNavigate]
  );

  return (
    <Sidebar
      header={<WorkspaceSwitcher />}
      action={kind === 'full' ? <SidebarAction actions={actionItems} /> : undefined}
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {/* Settings link (assessor shell only — elsewhere Settings is a nav item) */}
          {!settingsInNav ? <a
            href={`${prefix}/settings/profile`}
            onClick={handleNav(`${prefix}/settings/profile`)}
            aria-current={settingsActive ? 'page' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: '0 var(--sp-1)',
              minHeight: 44,
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--fs-sm)',
              fontFamily: 'inherit',
              textDecoration: 'none',
              color: settingsActive ? 'var(--accent)' : 'var(--text-muted)',
              background: settingsActive ? 'var(--accent-soft)' : 'transparent',
              fontWeight: settingsActive ? 500 : 400,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                flexShrink: 0,
                color: settingsActive ? 'var(--accent)' : 'var(--text-dim)',
              }}
            >
              {navIcon('settings')}
            </span>
            Settings
          </a> : null}

          {/* User identity */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: '0 var(--sp-1)',
              minHeight: 32,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                fontSize: 'var(--fs-xs)',
                fontWeight: 600,
                letterSpacing: '0.02em',
                background: 'var(--accent)',
                color: 'var(--text-inverse)',
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {avatarInitials}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 500,
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </div>
              {roleLabel ? (
                <div
                  style={{
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--text-muted)',
                    textTransform: 'capitalize',
                  }}
                >
                  {roleLabel}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      }
    >
      {allGroups.map((entry) => {
        // Top-level item (no group wrapper)
        if ('topLevel' in entry && entry.topLevel) {
          const tl = entry as NavTopLevel;
          // V109-5 punch #4: inside a program, Boundary opens THAT program's Scope (the open program, not the first one).
          const openProgram = /^programs\/([a-z0-9-]+)(?:\/|$)/.exec(relativePath)?.[1];
          const href = tl.id === 'boundary' && openProgram && isProgramKey(openProgram) ? `${prefix}/programs/${openProgram}/scope` : `${prefix}/${tl.href}`;
          const isActive = activeResult?.itemId === tl.id;
          const badge = badges.resolve(tl.badge);
          const badgeTitle = badges.resolveTitle(tl.badge);
          return (
            <SidebarTopLevel
              key={tl.id}
              href={href}
              icon={navIcon(tl.icon)}
              label={tl.label}
              active={isActive}
              badge={badge}
              badgeTitle={badgeTitle}
              disabled={tl.disabled}
              disabledLabel={tl.disabledLabel}
              indent={tl.indent}
              onClick={handleNav(href)}
            />
          );
        }

        // Collapsible group — controlled by useSidebarState (NAV-2-5)
        const group = entry as NavGroup;
        return (
          <SidebarGroup
            key={group.id}
            id={group.id}
            label={group.label}
            expanded={sidebar.isExpanded(group.id)}
            onToggle={() => sidebar.toggleGroup(group.id)}
          >
            {group.items.map((item: NavItem) => {
              const href = `${prefix}/${item.href}`;
              const isActive = activeResult?.itemId === item.id;
              const badge = badges.resolve(item.badge);
              const badgeTitle = badges.resolveTitle(item.badge);
              return (
                <SidebarItem
                  key={item.id}
                  href={href}
                  icon={navIcon(item.icon)}
                  label={item.label}
                  active={isActive}
                  badge={badge}
                  badgeTitle={badgeTitle}
                  disabled={item.disabled}
                  disabledLabel={item.disabledLabel}
                  meta={isProgramItem(item) ? <ProgramMeta item={item} /> : undefined}
                  onClick={handleNav(href)}
                />
              );
            })}
          </SidebarGroup>
        );
      })}
    </Sidebar>
  );
}

// -----------------------------------------------------------------------
// NAV-3-1: Mobile Drawer
// -----------------------------------------------------------------------

function MobileDrawer({
  onClose,
  prefix,
  groups,
  kind,
  isStaff,
  isAssessor,
  badges,
  user,
  settingsActive,
  workspaceSlug,
}: {
  onClose: () => void;
  prefix: string;
  groups: (NavGroup | NavTopLevel)[];
  kind: ShellKind;
  isStaff: boolean;
  isAssessor: boolean;
  badges: ReturnType<typeof useBadgeCounts>;
  user: { email?: string | null; name?: string | null; role?: string } | null;
  settingsActive: boolean;
  workspaceSlug: string;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // The DS Drawer owns modal semantics, focus trapping and return focus.
  // This component is mounted only by the mobile shell.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => { document.body.style.overflow = previous; };
  }, []);
  // V109-5 (design round 3, Blocker 3): while the drawer is open everything else is inert — main, the top bar,
  // the tab bar, a toast, the skip link — at every level from the drawer up to <body>; widening past the
  // tab-bar breakpoint closes it. Built-UI (Medium "Focus"): a layout effect, so inert is lifted before the
  // Drawer hands focus back to Menu (a passive cleanup ran after it and the focus landed on an inert button).
  useLayoutEffect(() => {
    const drawer = document.querySelector<HTMLElement>('[data-drawer].v75-nav-drawer');
    const made: HTMLElement[] = [];
    for (let el: HTMLElement | null = drawer; el && el !== document.body; el = el.parentElement) {
      for (const sib of Array.from(el.parentElement?.children ?? [])) {
        if (sib === el || !(sib instanceof HTMLElement) || sib.hasAttribute('data-drawer-overlay') || sib.hasAttribute('inert')) continue;
        sib.setAttribute('inert', ''); made.push(sib);
      }
    }
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 860px)') : null;
    const onWide = (e: MediaQueryListEvent) => { if (e.matches) onClose(); };
    mq?.addEventListener?.('change', onWide);
    return () => { for (const el of made) el.removeAttribute('inert'); mq?.removeEventListener?.('change', onWide); };
  }, [onClose]);

  return (
    <>
      {/* Overlay */}
      <div
        data-drawer-overlay
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--overlay-bg)',
          zIndex: 50,
        }}
        aria-hidden="true"
      />

      {/* Drawer panel: explicit mobile mode also covers narrow keyboard viewports. */}
      <Drawer open onClose={onClose} label="Navigation menu" forceDialog className="v75-nav-drawer">
        {/* Close button */}
        <button
          ref={closeButtonRef}
          onClick={onClose}
          aria-label="Close navigation menu"
          style={{
            position: 'absolute',
            top: 'var(--sp-2)',
            right: 'var(--sp-2)',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            padding: 0,
            zIndex: 1,
          }}
        >
          {navIcon('x')}
        </button>

        {/* Drawer content — reuses the full SidebarNav */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          }}
        >
          <SidebarNav
            prefix={prefix}
            groups={groups}
            kind={kind}
            isStaff={isStaff}
            isAssessor={isAssessor}
            badges={badges}
            user={user}
            settingsActive={settingsActive}
            workspaceSlug={workspaceSlug}
            onNavigate={onClose}
          />
        </div>
      </Drawer>
    </>
  );
}

// -----------------------------------------------------------------------
// NAV-3-3: Mobile Header Bar
// -----------------------------------------------------------------------

function MobileHeader({
  onMenuOpen,
  activeLabel,
  alertsCount,
  prefix,
  showBack,
}: {
  onMenuOpen: () => void;
  activeLabel: string;
  alertsCount: number;
  prefix: string;
  showBack: boolean;
}) {
  const navigate = useNavigate();

  return (
    <header
      data-mobile-header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        // V98-3-7: the notch inset is ADDED to the 48px bar (border-box), never taken out of it.
        boxSizing: 'border-box',
        height: 'calc(48px + env(safe-area-inset-top, 0px))',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: 'var(--surface-elevated)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 'max(var(--sp-1), env(safe-area-inset-left, 0px))',
        paddingRight: 'max(var(--sp-1), env(safe-area-inset-right, 0px))',
      }}
    >
      {/* Left slot: hamburger or back arrow */}
      <button
        onClick={showBack ? () => navigate(-1) : onMenuOpen}
        aria-label={showBack ? 'Go back' : 'Open navigation menu'}
        style={{
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          color: 'var(--text)',
          padding: 0,
          flexShrink: 0,
        }}
      >
        {navIcon(showBack ? 'arrow-left' : 'menu')}
      </button>

      {/* Center: page title */}
      <span
        style={{
          flex: 1,
          textAlign: 'center',
          fontSize: 'var(--fs-md)',
          fontWeight: 600,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 var(--sp-1)',
        }}
      >
        {activeLabel}
      </span>

      {/* Right: alerts bell with badge */}
      <button
        onClick={() => navigate(`${prefix}/alerts`)}
        aria-label={alertsCount > 0 ? `${alertsCount} alerts` : 'Alerts'}
        style={{
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          color: 'var(--text-muted)',
          padding: 0,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {navIcon('bell')}
        {alertsCount > 0 ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 999,
              background: 'var(--band-red-mark)',
              color: 'var(--text-inverse)',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
            }}
          >
            {alertsCount}
          </span>
        ) : null}
      </button>
    </header>
  );
}

// -----------------------------------------------------------------------
// V98-3-1: Mobile bottom tab bar (<= 859px)
// -----------------------------------------------------------------------

type MobileTab = 'today' | 'programs' | 'menu';

/** Tabs for the shell kind. Tabs never carry badges (no badged dead tab). */
export function mobileTabs(kind: ShellKind): Array<{ id: MobileTab; label: string; icon: string; href?: string }> {
  if (kind === 'minimal') return [{ id: 'today', label: 'Today', icon: 'home', href: 'today' }, { id: 'menu', label: 'More', icon: 'menu' }];
  if (kind === 'assessor') return [{ id: 'today', label: 'Overview', icon: 'home', href: 'dashboard' }, { id: 'menu', label: 'Menu', icon: 'menu' }];
  return [
    { id: 'today', label: 'Today', icon: 'home', href: 'today' },
    { id: 'programs', label: 'Programs', icon: 'layers', href: 'programs' },
    { id: 'menu', label: 'Menu', icon: 'menu' },
  ];
}

function MobileTabbar({
  prefix,
  kind,
  menuOpen,
  onMenuToggle,
  activeTab,
}: {
  prefix: string;
  kind: ShellKind;
  menuOpen: boolean;
  onMenuToggle: () => void;
  activeTab: MobileTab | null;
}) {
  const navigate = useNavigate();
  return (
    <nav
      aria-label="Quick navigation"
      data-mobile-tabbar
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'stretch',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
        background: 'var(--surface-elevated)',
        borderTop: '1px solid var(--border)',
        zIndex: 50,
      }}
    >
      {mobileTabs(kind).map((tab) => {
        const isActive = tab.id !== 'menu' && tab.id === activeTab;
        const style: React.CSSProperties = {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          minHeight: 48,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: isActive ? 'var(--accent)' : 'var(--text-muted)',
          fontSize: 'var(--fs-xs)',
          fontWeight: 500,
          fontFamily: 'inherit',
          padding: 0,
          textDecoration: 'none',
          WebkitTapHighlightColor: 'transparent',
        };
        const glyph = (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isActive ? 'var(--accent-soft)' : 'transparent',
              borderRadius: 999,
              padding: '4px 14px',
            }}
          >
            {navIcon(tab.icon)}
          </span>
        );
        if (!tab.href) {
          return (
            <button key={tab.id} type="button" onClick={onMenuToggle} aria-expanded={menuOpen} aria-haspopup="dialog" style={style}>
              {glyph}
              <span>{tab.label}</span>
            </button>
          );
        }
        const href = `${prefix}/${tab.href}`;
        return (
          <a
            key={tab.id}
            href={href}
            onClick={(e) => { e.preventDefault(); navigate(href); }}
            aria-current={isActive ? 'page' : undefined}
            style={style}
          >
            {glyph}
            <span>{tab.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

// -----------------------------------------------------------------------
// AppShell
// -----------------------------------------------------------------------

export function AppShell({ children }: { children: ReactNode }) {
  // V9-7-4: real-time cache invalidation via SSE.
  useWorkspaceEvents();

  const { user } = useAuth();
  const loc = useLocation();
  const workspace = useOptionalWorkspace();
  const isMobile = useIsMobile();

  const prefix = workspace ? `/w/${workspace.workspace.slug}/app` : '/app';
  const role = workspace?.role;
  const isAssessor = role === 'assessor';
  const isStaff = user?.role === 'admin';
  // R3-U12: the footer's role line is the viewer's role IN THIS WORKSPACE (membership), not the account's
  // global role — Dana is an owner of Northwind, not an "editor".
  const shellUser = user ? { ...user, role: role ?? user.role } : null;
  const scopeBar = workspace?.scopeBar;
  const scopeBarExpandedProps =
    isAssessor && scopeBar ? formatScopeBarExpanded(scopeBar) : null;

  // V98-3-1: viewer-scoped shell (assessor / minimal / full) — no role switcher.
  const navigation = useWorkspaceNavigation(workspace?.workspace.slug, role);
  const navGroups = navigation.groups;
  const shellKind = navigation.kind;

  const badges = useBadgeCounts(
    isAssessor ? undefined : workspace?.workspace.slug,
    isAssessor,
    navigation.owed,
  );

  // Active detection for settings
  const settingsActive = loc.pathname.startsWith(`${prefix}/settings`);

  // -----------------------------------------------------------------------
  // Mobile state
  // -----------------------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Built-UI (Medium "Focus"): Escape / Close return focus to the Menu button that opened the drawer.
  const menuOpener = useRef<HTMLElement | null>(null);
  const openDrawer = useCallback(() => { menuOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; setDrawerOpen(true); }, []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    window.requestAnimationFrame(() => { const el = menuOpener.current; if (el?.isConnected && !el.closest('[inert]')) el.focus(); });
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [loc.pathname]);

  // Compute active label + detail-page detection for mobile header
  const appPrefix = `${prefix}/`;
  const relativePath = loc.pathname.startsWith(appPrefix)
    ? loc.pathname.slice(appPrefix.length)
    : '';

  const allGroups = useMemo(() => {
    const result = [...navGroups];
    if (isStaff && !isAssessor) {
      result.push(STAFF_NAV_GROUP);
    }
    return result;
  }, [navGroups, isStaff, isAssessor]);

  const activeResult = findActiveItem(allGroups, relativePath);

  // Derive active label from nav config
  const activeLabel = useMemo(() => {
    if (!activeResult) {
      // Settings pages
      if (settingsActive) return 'Settings';
      return 'gacpro';
    }
    // Find the item label
    for (const entry of allGroups) {
      if ('topLevel' in entry && entry.topLevel) {
        if (entry.id === activeResult.itemId) return entry.label;
        continue;
      }
      const group = entry as NavGroup;
      if (group.id === activeResult.itemId) return group.label;
      for (const item of group.items) {
        if (item.id === activeResult.itemId) return item.label;
      }
    }
    return 'gacpro';
  }, [activeResult, allGroups, settingsActive]);

  // Detail page heuristic: if the current path has more segments than
  // the nav item's href, it's a detail page (show back arrow).
  const isDetailPage = useMemo(() => {
    if (!activeResult) return false;
    // Find the matching item's href
    for (const entry of allGroups) {
      if ('topLevel' in entry && entry.topLevel) {
        if (entry.id === activeResult.itemId) {
          const itemSegments = entry.href.split('/').length;
          const pathSegments = relativePath.split('/').length;
          return pathSegments > itemSegments;
        }
        continue;
      }
      const group = entry as NavGroup;
      for (const item of group.items) {
        if (item.id === activeResult.itemId) {
          const itemSegments = item.href.split('/').length;
          const pathSegments = relativePath.split('/').length;
          return pathSegments > itemSegments;
        }
      }
    }
    return false;
  }, [activeResult, allGroups, relativePath]);

  // Which bottom tab is active? Only the tab whose page is open (no default highlight).
  const activeTab: MobileTab | null = useMemo(() => {
    const home = shellKind === 'minimal' ? ['mine', 'today', 'dashboard'] : shellKind === 'assessor' ? ['dashboard'] : ['today', 'dashboard'];
    if (home.includes(relativePath)) return 'today';
    if (shellKind === 'full' && (relativePath === 'programs' || relativePath.startsWith('programs/'))) return 'programs';
    return null;
  }, [relativePath, shellKind]);

  // Section strip (full shell): the links of the nav section this page belongs to.
  // Never on Today or an item page — they are their own hub (V98-3 review U7: the strip overflowed at 390px).
  const ownHub = relativePath === 'today' || relativePath.startsWith('items/');
  const sectionNav = shellKind === 'full' && !ownHub ? <SectionNav prefix={prefix} relativePath={relativePath} /> : null;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (isMobile) {
    return (
      <div
        data-app-shell-root
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          background: 'var(--surface)',
          color: 'var(--text)',
        }}
      >
        {/* Skip link — first in DOM order, visible on focus (.skip-link moves in on :focus; V98-3 review U12). */}
        <a href="#main-content" className="skip-link">Skip to main content</a>

        {/* Mobile header */}
        <MobileHeader
          onMenuOpen={openDrawer}
          activeLabel={activeLabel}
          alertsCount={badges.alertsCritical}
          prefix={prefix}
          showBack={isDetailPage}
        />

        {/* Scope bar for assessors */}
        {scopeBarExpandedProps ? (
          <ScopeBarExpanded
            framework={scopeBarExpandedProps.framework}
            periodStart={scopeBarExpandedProps.periodStart}
            periodEnd={scopeBarExpandedProps.periodEnd}
            packageHash={scopeBarExpandedProps.packageHash}
            verifyHref={scopeBarExpandedProps.verifyHref}
            status={scopeBarExpandedProps.status}
            onCopyHash={
              scopeBarExpandedProps.packageHash
                ? () => navigator.clipboard.writeText(scopeBarExpandedProps.packageHash!)
                : undefined
            }
          />
        ) : null}

        {/* Main content */}
        <main
          id="main-content"
          tabIndex={-1}
          // V109-5 built-UI #10: the bottom padding is a class rule (index.css) so the toast's reserved space wins.
          className="v75-mobile-main"
          style={{
            flex: 1,
            minWidth: 0,
            // V98-3-7: safe-area insets at every breakpoint (landscape notch included).
            paddingTop: 'var(--sp-4)',
            paddingRight: 'max(var(--sp-4), env(safe-area-inset-right, 0px))',
            paddingLeft: 'max(var(--sp-4), env(safe-area-inset-left, 0px))',
          }}
        >
          {sectionNav}
          {children}
        </main>

        {/* Bottom tab bar (Today · Programs · Menu) */}
        <MobileTabbar
          prefix={prefix}
          kind={shellKind}
          menuOpen={drawerOpen}
          onMenuToggle={() => (drawerOpen ? closeDrawer() : openDrawer())}
          activeTab={activeTab}
        />

        {/* Drawer (portal-free, rendered last for z-index stacking) */}
        {drawerOpen ? (
          <MobileDrawer
            onClose={closeDrawer}
            prefix={prefix}
            groups={navGroups}
            kind={shellKind}
            isStaff={!!isStaff}
            isAssessor={isAssessor}
            badges={badges}
            user={shellUser}
            settingsActive={settingsActive}
            workspaceSlug={workspace?.workspace.slug ?? '_default'}
          />
        ) : null}
      </div>
    );
  }

  // Desktop layout
  return (
    <div
      data-app-shell-root
      style={{
        display: 'flex',
        minHeight: '100dvh',
        background: 'var(--surface)',
        color: 'var(--text)',
      }}
    >
      {/* Skip link — FIRST in DOM order (before the sidebar), visible on focus (V98-3 review U12). */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Desktop sidebar */}
      <div data-sidebar-sticky style={{ flexShrink: 0, position: 'sticky', top: 0 }}>
        <SidebarNav
          prefix={prefix}
          groups={navGroups}
          kind={shellKind}
          isStaff={!!isStaff}
          isAssessor={isAssessor}
          badges={badges}
          user={shellUser}
          settingsActive={settingsActive}
          workspaceSlug={workspace?.workspace.slug ?? '_default'}
        />
      </div>

      {/* Main content column */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* Scope bar for assessors */}
        {scopeBarExpandedProps ? (
          <ScopeBarExpanded
            framework={scopeBarExpandedProps.framework}
            periodStart={scopeBarExpandedProps.periodStart}
            periodEnd={scopeBarExpandedProps.periodEnd}
            packageHash={scopeBarExpandedProps.packageHash}
            verifyHref={scopeBarExpandedProps.verifyHref}
            status={scopeBarExpandedProps.status}
            onCopyHash={
              scopeBarExpandedProps.packageHash
                ? () => navigator.clipboard.writeText(scopeBarExpandedProps.packageHash!)
                : undefined
            }
          />
        ) : null}

        {/* Main content */}
        <main
          id="main-content"
          tabIndex={-1}
          style={{
            flex: 1,
            padding: 'var(--sp-5) max(var(--sp-5), env(safe-area-inset-right)) var(--sp-7) max(var(--sp-5), env(safe-area-inset-left))',
            maxWidth: 1360,
            margin: '0 auto',
            width: '100%',
          }}
        >
          {sectionNav}
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * `NavLink` is still exported for pages that render router-managed
 * links inside their bodies.
 */
export { NavLink };
